import React, { useCallback, useEffect, useState } from "react";
import Icon from "@/common/ui/Image";
import { SidebarItem } from "@/types/user.types";
import { ChevronRight, Menu, X } from "lucide-react";
import { Center } from "@/common/css";
import Button from "@/common/ui/Button";
import { SideBarItemContent } from "@/common/ui/SideBar";
import { useNavigationGuard } from "@/utils/helper/navigation";
import { GuardedLink } from "@/common/custom/GuardedLink";
import { useAppDispatch } from "@/store/store";
import { dashboardSelector, toggleSideBarExpended } from "@/store/features/dashboardSlice";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const SideBarItem: React.FC<{
  item: SidebarItem;
  isActive: boolean;
  isSideBarExpanded: boolean;
  onClick: () => void;
  contendSidebars: SidebarItem[] | undefined;
  isMobile?: boolean;
  onClickEffect?: () => void;
  mainSidebars?: SidebarItem[];
}> = React.memo(({ item, isActive, isSideBarExpanded, onClick, contendSidebars, onClickEffect, mainSidebars, isMobile }) => {
  const filteredContentSideBars = mainSidebars || contendSidebars?.filter((c) => !c.hideContentSideBar);
  const location = useLocation();

  return (
    <button
      onClick={onClick}
      className={`p-3 ${isSideBarExpanded ? "md:pl-[28px]" : "group relative"} ${
        isActive ? "bg-primaryOpacity-light  border-text-primary" : " border-sidebar-bg"
      } ${isSideBarExpanded ? "border-l-4" : "border-l-2"} pr-1 w-full`}
    >
      <SideBarItemContent contendSidebars={mainSidebars || contendSidebars} isExpanded={isSideBarExpanded} isMobile={isMobile ? onClickEffect : undefined}>
        <Icon src={item?.iconUrl || ""} alt="side-bar-icon" className="size-4" />
        {isSideBarExpanded && <h2 className=" text-white font-normal !leading-normal text-13">{item?.name}</h2>}
      </SideBarItemContent>
      {filteredContentSideBars ? (
        <div className="hidden group-hover:block absolute top-0 left-11 px-1.5" onClick={(e) => e.stopPropagation()}>
          <div className="h-full bg-sidebar-bg rounded">
            <h3 className="pt-2 text-white font-bold !leading-normal text-13 px-4 text-start">{item.name}</h3>
            <ul className="w-full text-nowrap bg-sidebar-bg rounded flex flex-col py-2">
              {filteredContentSideBars?.map((contentLink: any, index) => {
                const isActive = location.pathname === contentLink?.key;
                return (
                  <GuardedLink to={contentLink.key} key={index} onClickEffect={isMobile ? onClickEffect : undefined}>
                    <li
                      className={`text-white py-2 text-13 !leading-normal px-4 hover:cursor-pointer w-full hover:text-white/60 hover:bg-primaryOpacity-10 ${
                        isActive ? " font-semibold bg-primaryOpacity-10/60" : ""
                      } `}
                    >
                      <div className="flex items-center gap-2 justify-start w-full">
                        {contentLink?.icon && <span className="w-4 h-4">{contentLink?.icon}</span>}
                        <span className="text-start">{contentLink.name}</span>
                      </div>
                    </li>
                  </GuardedLink>
                );
              })}
            </ul>
          </div>
        </div>
      ) : (
        !isSideBarExpanded && (
          <div className="hidden group-hover:block absolute top-0 left-12 h-fit bg-sidebar-bg rounded">
            <h3 className="py-2 text-white font-bold !leading-normal text-13 px-4 text-start">{item.name}</h3>
          </div>
        )
      )}
    </button>
  );
});

interface SideBarProps {
  sideBars: SidebarItem[];
  activeSideBarKey: string | null;
}

const SideBar: React.FC<SideBarProps> = ({ sideBars, activeSideBarKey }) => {
  const customNavigate = useNavigationGuard();
  const dispatch = useAppDispatch();
  const { isSideBarExpanded } = useSelector(dashboardSelector);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if screen is mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleNavigation = useCallback(
    (item: SidebarItem) => {
      const contentKey = item?.path || item.content?.defaultKey || item.key;
      customNavigate(contentKey);
      if (isMobile) {
        setIsMobileMenuOpen(false);
      }
    },
    [customNavigate, isMobile]
  );

  const handleToggleExpanded = () => {
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      dispatch(toggleSideBarExpended());
    }
  };
  // console.log(sideBars);
  // Mobile hamburger menu
  if (isMobile) {
    return (
      <>
        {/* Mobile menu button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="fixed top-2.5 left-2 z-50 p-1 bg-sidebar-bg text-white rounded-lg shadow-lg md:hidden"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Mobile full-screen sidebar */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 bg-sidebar-bg md:hidden">
            <div className="flex flex-col h-full">
              {/* Mobile header */}
              <div
                className="flex items-center justify-between w-full  shadow-xl"
                style={{
                  background: "linear-gradient(90deg,rgba(49, 29, 77, 1) 0%, rgba(79, 62, 92, 1) 100%, rgba(237, 221, 83, 1) 100%);",
                }}
              >
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="fixed top-2.5 left-2 z-50 p-1 bg-sidebar-bg text-white rounded-lg shadow-lg md:hidden"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-5 h-5" />}
                </button>
                <div className="p-3 md:p-6 flex items-center gap-x-3 w-full border-b border-sidebar-bg/20 justify-center md:justify-start">
                  <Icon src="/icons/logo.png" alt="logo" className="size-6" />
                  <div className="text-white text-[1.4375rem] leading-[normal]">lystproof</div>
                </div>
              </div>

              {/* Mobile navigation */}
              <nav className="flex-1 overflow-y-auto pt-4">
                {sideBars
                  .filter((d) => !d?.isHidden)
                  .map((item) => {
                    const isTabActive = item.key === activeSideBarKey;
                    const contendSidebars = item.content?.sidebars;
                    return (
                      <SideBarItem
                        key={item.key}
                        item={item}
                        isSideBarExpanded={true}
                        isActive={isTabActive}
                        contendSidebars={contendSidebars}
                        mainSidebars={item.content?.mainSidebars}
                        onClick={() => handleNavigation(item)}
                        isMobile={true}
                        onClickEffect={() => setIsMobileMenuOpen(false)}
                      />
                    );
                  })}
              </nav>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop sidebar
  return (
    <div className={`hidden md:flex h-full ${isSideBarExpanded ? " min-w-50" : "min-w-12"} bg-sidebar-bg flex-col justify-between shadow-lg z-40`}>
      <div className="flex flex-col items-center w-full">
        <div className={`p-3 flex items-center gap-x-1 w-full ${isSideBarExpanded ? "pl-8" : ""}`}>
          <Icon src="/icons/logo.png" alt="logo" className="size-6" />
          {isSideBarExpanded && <div className="text-white text-[1.4375rem] leading-[normal]">lystproof</div>}
        </div>
        <nav className="flex flex-col mt-2 w-full">
          {sideBars
            .filter((d) => !d?.isHidden)
            .map((item) => {
              const isTabActive = item.key === activeSideBarKey;
              const contendSidebars = item.content?.sidebars;
              return (
                <SideBarItem
                  key={item.key}
                  item={item}
                  isSideBarExpanded={isSideBarExpanded}
                  isActive={isTabActive}
                  contendSidebars={contendSidebars}
                  mainSidebars={item.content?.mainSidebars}
                  onClick={() => handleNavigation(item)}
                />
              );
            })}
        </nav>
      </div>
      <Center className={`${isSideBarExpanded ? "bg-sidebar-expandBtn shadow-expandBtn" : ""} `}>
        <Button variant="transparent" size="none" onClick={handleToggleExpanded}>
          <ChevronRight className={`text-white ${isSideBarExpanded ? " rotate-180" : ""}`} />
        </Button>
      </Center>
    </div>
  );
};

export default React.memo(SideBar);
